import { useRef } from "react";
import { ChevronLeft, ChevronRight, GraduationCap, MessageCircleQuestion } from "lucide-react";
import { Section, SectionHeading } from "@/components/ui";
import { MENTORS } from "@/data/home";

const SHOW_SAMPLE_NOTE = process.env.NODE_ENV !== "production" && MENTORS.sample;

/** Full literals so Tailwind can see every class. */
const MENTOR_TONES = {
  brand: {
    card: "border-brand-200 hover:border-brand-500",
    photo: "bg-brand-100",
    initials: "text-brand-500",
    pill: "border-brand-500",
    pillIcon: "bg-brand-500 text-white",
  },
  sun: {
    card: "border-brand-200 hover:border-brand-500",
    photo: "bg-brand-50",
    initials: "text-brand-600",
    pill: "border-brand-200",
    pillIcon: "bg-brand-500 text-white",
  },
};

function initialsOf(name) {
  const parts = name.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

/** One mentor: photo on top (initials show if the image cannot load), then name, role and a one-line bio. */
function MentorCard({ person, tone }) {
  const t = MENTOR_TONES[tone];
  return (
    <article
      data-testid={`mentor-${person.id}`}
      className={`group flex h-full flex-col overflow-hidden rounded-3xl border bg-white text-ink transition-all duration-300 hover:shadow-soft ${t.card}`}
    >
      <div className="relative">
        <div className={`relative aspect-square ${t.photo}`}>
          <span
            className={`absolute inset-0 flex items-center justify-center rounded-t-3xl font-heading text-4xl font-bold sm:text-5xl ${t.initials}`}
            aria-hidden="true"
          >
            {initialsOf(person.name)}
          </span>
          {person.image ? (
            <img
              src={person.image}
              alt={`${person.name}, ${person.role}`}
              width={500}
              height={500}
              loading="lazy"
              decoding="async"
              onError={(event) => {
                event.currentTarget.style.visibility = "hidden";
              }}
              className="absolute inset-0 h-full w-full rounded-t-3xl object-cover object-top transition-[filter] duration-500 group-hover:brightness-[1.04]"
            />
          ) : null}
        </div>

        {person.org ? (
          <span
            className={`absolute bottom-0 left-3 z-10 flex max-w-[calc(100%-1.5rem)] translate-y-1/2 items-center gap-1.5 rounded-full border bg-white py-1 pl-1 pr-3 text-xs font-bold leading-none text-ink shadow-soft transition-transform duration-300 group-hover:translate-y-[calc(50%-3px)] sm:left-4 ${t.pill}`}
          >
            <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${t.pillIcon}`} aria-hidden="true">
              <GraduationCap className="h-3.5 w-3.5" />
            </span>
            <span className="truncate">{person.org}</span>
          </span>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-1 p-3.5 pt-6 sm:p-4 sm:pt-7">
        <h4 className="font-heading text-lg font-bold leading-tight sm:text-xl">{person.name}</h4>
        <p className="text-sm font-bold leading-snug">{person.role}</p>
        <p className="line-clamp-2 text-sm text-ink/70">{person.note}</p>
      </div>
    </article>
  );
}

export function MentorsSection() {
  const trackRef = useRef(null);

  const scrollByCard = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    const slide = el.querySelector(":scope > li");
    const step = slide ? slide.offsetWidth + 12 : Math.round(el.clientWidth * 0.25);
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  return (
    <Section labelledBy="mentors-title" testId="home-mentors">
      <SectionHeading id="mentors-title" title={MENTORS.title} highlight="come alive" intro={MENTORS.intro} />

      <ul className="mt-8 grid gap-3 sm:grid-cols-3">
        {MENTORS.questions.map((question) => (
          <li key={question}>
            <p className="flex h-full gap-3 rounded-3xl border-l-8 border-brand-200 bg-brand-50 p-5 text-base font-semibold sm:text-lg">
              <MessageCircleQuestion className="mt-0.5 h-6 w-6 shrink-0 text-brand-500" aria-hidden="true" />
              <span>&ldquo;{question}&rdquo;</span>
            </p>
          </li>
        ))}
      </ul>

      <div className="mt-12 flex items-end justify-between gap-4">
        <h3 className="font-heading text-2xl font-bold sm:text-3xl">{MENTORS.carouselTitle}</h3>
        <div className="hidden gap-2 sm:flex">
          <button
            type="button"
            onClick={() => scrollByCard(-1)}
            aria-label="Previous mentors"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-brand-500 text-brand-500 transition-colors hover:bg-brand-500 hover:text-white"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => scrollByCard(1)}
            aria-label="Next mentors"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-brand-500 text-brand-500 transition-colors hover:bg-brand-500 hover:text-white"
          >
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>

      {SHOW_SAMPLE_NOTE ? (
        <p className="mt-3 rounded-2xl border border-dashed border-brand-200 bg-brand-50 px-4 py-2 text-sm">
          Sample mentors (development only). Replace in <code>src/data/home.js</code>.
        </p>
      ) : null}

      <ul
        ref={trackRef}
        tabIndex={0}
        aria-label="Mentors, scroll sideways"
        data-testid="mentor-carousel"
        className="snap-row -mx-4 mt-5 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 pb-3 sm:mx-0 sm:scroll-px-0 sm:px-0 lg:gap-4"
      >
        {MENTORS.people.map((person, i) => (
          <li
            key={person.id}
            className="w-[78%] shrink-0 snap-start sm:w-[calc((100%-0.75rem)/2)] lg:w-[calc((100%-3rem)/4)]"
          >
            <MentorCard person={person} tone={i % 2 === 0 ? "brand" : "sun"} />
          </li>
        ))}
      </ul>

      {MENTORS.footnote ? <p className="mt-4 max-w-[60ch] text-sm text-ink/70">{MENTORS.footnote}</p> : null}
    </Section>
  );
}


